import { AfterViewInit, Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { ViewerConstants } from "../../shared/model/viewer-constants";
import { GeoService } from "../../viewer-components/services/internal-services/geo.service";
import { BaseViewerService } from "../../viewer-components/services/internal-services/viewer-services/base-viewer.service";
import { ImageGalleryService } from "../../viewer-components/services/internal-services/image-gallery/image-gallery.service";
import { ViewerConfigurationService } from "../../viewer-components/services/viewer-configuration.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../shared/model/google-analytics-constants";
import { ReportService } from "../../viewer-components/services/internal-services/report.service";
import { ImagesService } from "../../viewer-components/services/internal-services/images.service";
import { IProjectAOI } from "../../viewer-components/interfaces/i-project-aoi";
import { ProjectService } from "../../viewer-components/services/internal-services/project.service";
import { ElementsService } from "../../viewer-components/services/internal-services/elements.service";
import { Observable, of } from "rxjs";
import { GoogleAnalyticsService } from "../../shared/services/google-analytics.service";

// Interface extending IProjectAOI with a selected property
interface IReportProject {
  selected: boolean; // Property to indicate if the project is selected
  fixed: boolean; // Force to send in the report, usually newest aoi for wide and urban/gray
  report_date: string;
  report_name: string;
  aoi: string;
  thumb: string;
}

@Component({
  selector: "property-report",
  templateUrl: "./property-report.component.html",
  styleUrls: ["./property-report.component.scss"],
  providers: [MatDialogConfig],
  standalone: false,
})
export class PropertyReportComponent implements OnInit, AfterViewInit {
  reportTypes = ViewerConstants.REPORT_TYPES;

  reportSelected: number = this.reportTypes.NONE;

  location: number[];

  viewerService: BaseViewerService;

  reportService: ReportService;

  galleryService: ImageGalleryService;

  googleAnalyticsService: GoogleAnalyticsService;

  address: string;

  formatDate: string;

  isLoading: boolean = false;

  reportLoaded: boolean = false;

  enableDamageAssessment: boolean = false;

  reportDialog: any;

  unitsImperial: boolean = true;

  reportError = null;

  projects: {
    urban: IReportProject[];
    wide: IReportProject[];
    graysky: IReportProject[];
    ir: IReportProject[];
  };

  thumbnail: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private matDialog: MatDialog,
    private viewerConfigurationService: ViewerConfigurationService,
    private elementsService: ElementsService,
  ) {}

  ngOnInit(): void {
    this.viewerService = this.data.viewerService;
    this.reportService = this.data.reportService;
    this.googleAnalyticsService = this.data.googleAnalyticsService;
    this.galleryService = this.data.galleryService;
    this.enableDamageAssessment = this.data.enableDamageAssessment;
    const { coordinates } = this.data;
    this.location = coordinates;
    this.address = this.data.address;
    this.projects = this.fetchProjects(this.data.aois);

    this.reportLoaded = true;
  }

  ngAfterViewInit(): void {
    this.reportDialog = this.matDialog.getDialogById("property-report-dialog");

    // Check global measurement system first
    const globalSystem = this.viewerConfigurationService.getGlobalMeasurementSystem();
    if (
      globalSystem === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.METRIC ||
      globalSystem === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL
    ) {
      this.unitsImperial = globalSystem === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL;
    } else {
      const initialPropertyUnit = this.viewerService.getPropertyInfoUnit?.() ?? null;
      if (initialPropertyUnit !== null && initialPropertyUnit !== undefined) {
        this.unitsImperial = initialPropertyUnit === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL;
      } else {
        this.unitsImperial = this.viewerService.getMeasurementSystemId() === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL;
      }
    }

    // Subscribe to global changes
    this.viewerConfigurationService.globalMeasurementSystem$.subscribe((systemId) => {
      if (
        systemId === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.METRIC ||
        systemId === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL
      ) {
        this.unitsImperial = systemId === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL;
      }
    });

    this.viewerService.propertyInfoUnit$?.subscribe((unit) => {
      if (unit !== null && unit !== undefined) {
        this.unitsImperial = unit === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL;
      }
    });
  }

  selectReportType(type: number): void {
    this.isLoading = true;
    this.reportSelected = type;
    switch (type) {
      case this.reportTypes.DAMAGE:
        this.thumbnail = this.projects.graysky[0]?.thumb;
        this.formatDate = this.projects.graysky[0]?.report_date;
        this.reportDialog.updateSize("1178px", "694px");
        this.googleAnalyticsService.setGoogleAnalyticsEvent(
          GoogleAnalyticsConstantsEvents.CLICK_NEXT_ELEMENTS_DAMAGE_REPORT,
          GoogleAnalyticsConstantsCategory.PROPERTY_REPORT,
          true,
        );
        break;
      case this.reportTypes.PROPERTY:
        this.thumbnail = this.projects.urban[0]?.thumb;
        this.formatDate = this.projects.urban[0]?.report_date;
        this.reportDialog.updateSize("1178px", "694px");
        this.googleAnalyticsService.setGoogleAnalyticsEvent(
          GoogleAnalyticsConstantsEvents.CLICK_NEXT_ELEMENTS_PROPERTY_REPORT,
          GoogleAnalyticsConstantsCategory.PROPERTY_REPORT,
          true,
        );
        break;
      case this.reportTypes.NONE:
        this.reportDialog.updateSize("38%", "50%");
        break;
      default:
        break;
    }
  }

  changeUnits(event: any): void {
    this.unitsImperial = event.value;
    const unit = this.unitsImperial
      ? ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL
      : ViewerConstants.MEASUREMENT_SYSTEMS.IDS.METRIC;
    this.viewerService.updatePropertyInfoUnit(unit);
    this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
  }

  private reportDate(aoi: IProjectAOI): string {
    return aoi.isGraySky()
      ? `${aoi.nadirDate.toLocaleString("en", {
          month: "short",
        })} ${aoi.nadirDate.getDate()}, ${aoi.nadirDate.getFullYear()}`
      : `${aoi.nadirDate.toLocaleString("en", {
          month: "short",
        })} ${aoi.nadirDate.getFullYear()}`;
  }

  private reportName(name: string, date: string): string {
    if (!name || !date) {
      return "";
    }
    return `${name.replace("-", " ")} - ${date}`;
  }

  downloadReport(): void {
    this.viewerService.setReportReady(false);

    let route: string;
    let templateName: string;
    if (this.reportSelected === this.reportTypes.PROPERTY) {
      route = "property";
      templateName = this.viewerConfigurationService.getViewerConfiguration().propertyAttributesTemplate;
      this.googleAnalyticsService.setGoogleAnalyticsEvent(
        GoogleAnalyticsConstantsEvents.DOWNLOAD_ELEMENTS_PROPERTY_REPORT,
        GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
        true,
      );
    } else {
      route = "damage";
      templateName = this.viewerConfigurationService.getViewerConfiguration().damageAssessmentTemplate;
      this.googleAnalyticsService.setGoogleAnalyticsEvent(
        GoogleAnalyticsConstantsEvents.DOWNLOAD_ELEMENTS_DAMAGE_REPORT,
        GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
        true,
      );
    }

    this.fetchReport(route, this.location, templateName, this.unitsImperial);

    const message =
      this.reportSelected === this.reportTypes.PROPERTY ? " elements property report" : " elements damage report";
    this.reportService.setProgressBarMessage("Generating " + this.address + message + "...");

    this.reportDialog.close();
  }

  fetchReport(type: string, location: number[], template: string, imperial: boolean) {
    const wkt = GeoService.pointToWkt(location);
    const urban = this.projects.urban.filter((p) => p.selected && !p.fixed).map((p) => p.aoi);
    const wide = this.projects.wide.filter((p) => p.selected && !p.fixed).map((p) => p.aoi);
    const graysky = this.projects.graysky.filter((p) => p.selected && !p.fixed).map((p) => p.aoi);
    const ir = this.projects.ir.filter((p) => p.selected && !p.fixed).map((p) => p.aoi);
    const units = imperial ? "imperial" : null;
    this.elementsService.downloadPropertyReport(type, wkt, template, urban, wide, graysky, ir, units).subscribe({
      next: (response: any) => {
        try {
          const objectURL = URL.createObjectURL(response);

          if (response.size > 10000) {
            const anchor = document.createElement("a");
            anchor.href = objectURL;
            anchor.download = `${this.address} ${
              this.reportSelected === this.reportTypes.PROPERTY
                ? "elements property report.pdf"
                : "elements damage report.pdf"
            }`;

            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);

            URL.revokeObjectURL(objectURL);

            this.reportError = null;
          } else {
            this.reportError = `Error requesting report. No result available`;
          }
        } catch (err) {
          this.reportError = `Error requesting report. No result available`;
        }

        this.reportService.setProgressBarMessage(this.reportError);
      },
      error: () => {
        this.reportError = `Error requesting report (no data available)`;
      },
    });

    this.viewerService.setReportReady(true);
  }

  getThumbnail(project: IProjectAOI): string {
    const extent = GeoService.polygonToWkt(
      GeoService.createBufferAroundPoint({ coordinates: this.location, type: "Point" }, 40, {
        units: "meters",
        steps: 2,
      }),
    );
    const image = project.images.find((i) => ImagesService.isNadir(i.orientation) && i.child_aoi === project.getName());
    if (image) {
      return this.galleryService.getThumbnailURLForImage(image, extent)?.fullScreenTileURl;
    } else {
      return this.galleryService.getThumbnailURL(project.getLayerName(), project.getName(), extent);
    }
  }

  /**
   * process aois to convert in report projects
   * split in collection types
   * change name and date for showing
   * @param aois
   */
  fetchProjects(aois: IProjectAOI[]) {
    const urbanProjects = ProjectService.findUrban(aois);
    const grayskyProjects = ProjectService.findGraySky(aois);
    const urban = this.toReportProject(urbanProjects) || [];
    const wide = this.toReportProject(ProjectService.findWide(aois)) || [];
    const graysky = this.toReportProject(grayskyProjects) || [];

    // find first IR urban and first IR graysky, change collection name
    const irUrban = this.toReportProject(urbanProjects.filter((i) => i.hasInfrared()))?.[0] || null;
    const irGray = this.toReportProject(grayskyProjects.filter((i) => i.hasInfrared()))?.[0] || null;

    const ir = [irUrban, irGray]
      .filter((i) => i != null)
      .map((i) => {
        i.report_name = `CIR ${i.report_name}`;
        return i;
      });

    // select first aoi (newest) to fix in project
    if (urban?.length) {
      urban[0].fixed = true;
    }
    if (wide?.length) {
      wide[0].fixed = true;
    }
    if (this.reportSelected === this.reportTypes.DAMAGE && graysky?.length) {
      graysky[0].fixed = true;
    }

    return {
      urban,
      wide,
      graysky,
      ir,
    };
  }

  toReportProject(aois: IProjectAOI[]): IReportProject[] {
    return aois.map((aoi) => {
      const date = this.reportDate(aoi);

      let name = this.reportName(aoi.collectionName || aoi.name, date);
      if (aoi.isGraySky()) {
        name += ` ${aoi.getPrettyName()}`;
      }
      return {
        aoi: aoi.getName(),
        selected: false,
        fixed: false,
        report_name: name,
        report_date: date,
        thumb: this.getThumbnail(aoi),
      };
    });
  }

  selectedProjects(): Observable<IReportProject[]> {
    return of([
      ...this.projects.urban.filter((p) => p.selected),
      ...this.projects.graysky.filter((p) => p.selected),
      ...this.projects.ir.filter((p) => p.selected),
    ]);
  }

  fixedProjects(): Observable<IReportProject[]> {
    return of([
      ...this.projects.urban.filter((p) => p.fixed),
      ...this.projects.graysky.filter((p) => p.fixed),
      ...this.projects.wide.filter((p) => p.fixed),
    ]);
  }
}
